import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import {
  Container,
  CancelBtnContainer,
  TitleContainer,
  ContentContainer,
  TopSectionContainer,
  OrgUnitListContainer,
  OrgUnitList,
  BottomSection,
  AgreeContainer,
  AgreeCheckBoxContainer,
  TermContainer,
  AgreeAnnotationContainer,
  BtnContainer,
} from "./GoogleWorkspaceOrgUnitMemberIntegrationModal.styles";
import UI from "@mile-meeting/mile-ui";
import Callout from "uis/Callout";
import TreeNode, { OrgNode } from "./TreeNode/TreeNode";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "600px",
    borderRadius: "20px",
    transform: "translate(-50%, -50%)",
    padding: 0,
    margin: 0,
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

//* 트리 구조를 생성하는 함수
const buildTree = (paths: string[]) => {
  const root: Record<string, OrgNode> = {};
  for (const path of paths) {
    const parts = path.split("/").filter(Boolean);
    let current = root;
    for (const part of parts) {
      if (!current[part]) {
        current[part] = { _children: {} };
      }
      current = current[part]._children;
    }
  }
  return root;
};

export type GoogleWorkspaceOrgUnitMemberIntegrationModalProps = {
  modalIsOpen: boolean;
  orgUnitPathList?: string[];
  syncedOrgUnitList?: string[];
  isResync?: boolean;
  closeModal: () => void;
  onHandleDomainSync?: () => void;
  onHandleSync?: (paths: string[]) => void;
  onHandleReSync?: (paths: string[]) => void;
};

const GoogleWorkspaceOrgUnitMemberIntegrationModal: React.FC<GoogleWorkspaceOrgUnitMemberIntegrationModalProps> = ({
  isResync,
  modalIsOpen,
  orgUnitPathList = [],
  syncedOrgUnitList = [],
  closeModal,
  onHandleDomainSync,
  onHandleSync,
  onHandleReSync,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [checkedAgree, setCheckedAgree] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean | undefined>>({});
  const [loading, setLoading] = useState(false);
  const treeData = buildTree(orgUnitPathList);

  useEffect(() => {
    if (!modalIsOpen) return;

    //* 현재 expanded 상태를 초기화합니다. (모두 열려있는 상태)
    setExpanded(getExpandedInitialState(orgUnitPathList));
    //* 모든 경로의 selected 상태를 초기화합니다. (모두 선택 해제된 상태)
    if (syncedOrgUnitList && syncedOrgUnitList.length === 0) {
      setSelected(getSelectedInitialState(orgUnitPathList));
      return;
    }
    setSelected(getSelectedInitialStateFromSyncedOrgUnitList(syncedOrgUnitList));
    //* 이미 동기화된 경로는 선택된 상태로 설정합니다.
  }, [modalIsOpen]);

  //* INFO: 상위 경로를 기반으로 expanded 상태를 설정하는 함수
  const getExpandedInitialState = (paths: string[]) => {
    const expandedState: Record<string, boolean> = {};

    paths.forEach((path) => {
      const parts = path.split("/").filter(Boolean);

      //* 상위 경로들을 누적하면서 expanded 상태로 설정
      let current = "";
      for (let i = 0; i < parts.length - 1; i++) {
        current += "/" + parts[i];
        expandedState[current] = true;
      }
    });
    return expandedState;
  };

  //* INFO: 모든 경로를 기반으로 selected 상태를 설정하는 함수
  const getSelectedInitialState = (paths: string[]) => {
    const selectedState: Record<string, boolean> = {};

    paths.forEach((path) => {
      selectedState[path] = false;
    });

    return selectedState;
  };

  //* INFO: 선택된 경로를 기반으로 요청 보낼 데이터를 생성하는 함수
  const transformSelectPathToRequestFormat = (selected: Record<string, boolean | undefined>, allPaths: string[]) => {
    const result = new Set<string>();
    const pathSet = new Set(allPaths);

    const sortedPaths = [...allPaths].sort((a, b) => b.split("/").length - a.split("/").length);

    for (const path of sortedPaths) {
      if (!pathSet.has(path)) continue;

      const value = selected[path];
      if (value !== true) continue;

      // 상위 경로 중 하나라도 true면, 현재 path는 포함 X (상위로 대체)
      const parts = path.split("/").filter(Boolean);
      let hasSelectedAncestor = false;
      for (let i = 1; i < parts.length; i++) {
        const parentPath = "/" + parts.slice(0, i).join("/");
        if (selected[parentPath] === true) {
          hasSelectedAncestor = true;
          break;
        }
      }

      if (!hasSelectedAncestor) {
        result.add(path);
      }
    }

    return [...result];
  };

  //* INFO: 이미 동기화된 경로를 기반으로 selected 상태를 설정하는 함수
  const getSelectedInitialStateFromSyncedOrgUnitList = (syncedOrgUnitList: string[]) => {
    const selectedState: Record<string, boolean | undefined> = {};

    // 전체 false 초기화
    orgUnitPathList.forEach((path) => {
      selectedState[path] = false;
    });

    // 선택된 경로와 그 하위 true
    syncedOrgUnitList.forEach((selectedPath) => {
      orgUnitPathList.forEach((path) => {
        if (path === selectedPath || path.startsWith(selectedPath + "/")) {
          selectedState[path] = true;
        }
      });
    });

    // 상위로 undefined propagate
    const markParentAsIndeterminate = (path: string) => {
      const parts = path.split("/").filter(Boolean);
      while (parts.length > 1) {
        parts.pop();
        const parentPath = "/" + parts.join("/");
        if (selectedState[parentPath] !== true) {
          selectedState[parentPath] = undefined;
        }
      }
    };

    syncedOrgUnitList.forEach((selectedPath) => {
      markParentAsIndeterminate(selectedPath);
    });

    return selectedState;
  };

  const someSelected = Object.values(selected).some((value) => value === true);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelBtnContainer>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelBtnContainer>
        <TitleContainer>
          <UI.Icon name="google" size={24} />
          <UI.Typography type="heading02" fontStyle="bold" color="var(--alias-color-label-neutral)">
            Google Workspace 멤버 정보 연동
          </UI.Typography>
        </TitleContainer>
        <ContentContainer>
          <TopSectionContainer>
            <Callout
              context={`GWS에서 확인된 조직 목록입니다. 연동을 원하시는 조직을 선택하시면\n해당 조직에 속한 계정만 연동 및 동기화가 진행됩니다.\n연동이 완료되면 GWS 멤버 정보 변동 발생 시, 감지하여 안내해 드립니다.`}
            />
            <OrgUnitListContainer>
              <OrgUnitList>
                {treeData &&
                  Object.entries(treeData).map(([name, node]) => (
                    <TreeNode
                      key={`${name}_${node}}`}
                      name={name}
                      node={node}
                      level={0}
                      path={`/${name}`}
                      expanded={expanded}
                      setExpanded={setExpanded}
                      selected={selected}
                      setSelected={setSelected}
                    />
                  ))}
              </OrgUnitList>
            </OrgUnitListContainer>
          </TopSectionContainer>
          <BottomSection>
            <AgreeContainer>
              <AgreeCheckBoxContainer>
                <TermContainer onClick={() => setCheckedAgree(!checkedAgree)}>
                  <UI.CheckBox selected={checkedAgree} />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                      이용약관 동의
                    </UI.Typography>
                    <UI.Typography type="body02 normal" fontStyle="medium" color="var(--alias-color-primary-normal)">
                      <span>(</span>필수<span>)</span>
                    </UI.Typography>
                  </div>
                </TermContainer>
                <UI.ButtonGroups.Text
                  onClick={() => {
                    window.open("https://policy.mile.im/service-agree/2025.03.28.html");
                  }}
                  title="전체 보기"
                  size="small"
                  type="secondary"
                  styles={{
                    text: {
                      textDecoration: "underline",
                    },
                  }}
                />
              </AgreeCheckBoxContainer>
              <AgreeAnnotationContainer>
                <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-alternative)">
                  멤버 동기화 시, GWS 멤버들이 별도의 절차없이 Mile 회원으로 가입됩니다.
                </UI.Typography>
              </AgreeAnnotationContainer>
            </AgreeContainer>
            <BtnContainer>
              <UI.Button
                disabled={!checkedAgree || loading || !someSelected}
                title={"연동하기"}
                onClick={async () => {
                  if (!checkedAgree || loading || !someSelected) return;
                  setLoading(true);
                  if (!isResync) onHandleSync?.(transformSelectPathToRequestFormat(selected, orgUnitPathList));
                  if (isResync) await onHandleReSync?.(transformSelectPathToRequestFormat(selected, orgUnitPathList));
                  setLoading(false);
                }}
                size="large"
                style={{
                  width: "100%",
                }}
              />
              <div style={{ padding: "8px 0" }}>
                <UI.ButtonGroups.Text title="도메인 단위로 연동하기" type="secondary" onClick={onHandleDomainSync} />
              </div>
            </BtnContainer>
          </BottomSection>
        </ContentContainer>
      </Container>
    </Modal>
  );
};

export default React.memo(GoogleWorkspaceOrgUnitMemberIntegrationModal);
